import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import Papa from 'papaparse';
import classnames from 'classnames';
import { get } from 'lodash';
import { Button, FormFile } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleMinus } from '@fortawesome/pro-regular-svg-icons';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { connect, useDispatch } from 'react-redux';
import ToolkitProvider, { CSVExport } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import cellEditFactory from 'react-bootstrap-table2-editor';

import LanguagesPageShell from '../../Template/Languages/LanguagesPageShell';
import HtmlPreviewButton, { looksLikeHtml } from '../../Template/Languages/HtmlPreviewButton';
import AddLanguageModal from './AddLanguageModal';
import RoundButton from '../../../components/RoundButton';
import TextArea from '../../Template/Languages/LanguagesTable/TextAreaEditor';
import { useSelfieCamConfig } from '../hooks';
import { fetchEvent } from '../../Template/api';
import { updateEvent as updateEventInStore } from '../../../store/actions/events';
import { updateEventsGeneric } from '../../../helpers/api';
import {
  setLanguageRows,
  setLocaleOrder,
  updateLanguageCell,
  deleteLanguage,
  openAddLanguageModal,
} from './actions';
import {
  flattenLanguage,
  groupedToFlat,
  getLocalesFromRows,
  getAvailableLocales,
  groupedToLanguageObject,
  parseCsvToLanguageImport,
} from './utils';
import { displayErrorModal, displaySuccessModal } from '../../../helpers/swal';

import style from '../../Template/Languages/LanguagesTable/style.module.scss';
import pageStyle from './style.module.scss';

const { ExportCSVButton } = CSVExport;

const editorRenderer = (editorProps, value) => (
  <TextArea {...editorProps} value={value} />
);

const AiTemplateLanguage = ({
  slugDate,
  event,
  template,
  allEvents,
  languageRows,
  localeOrder,
  setLanguageRowsAction,
}) => {
  const dispatch = useDispatch();
  const { config, isLoading } = useSelfieCamConfig(slugDate);
  const [tableKey, setTableKey] = useState(0);
  const [changesMap, setChangesMap] = useState({});

  const languageObj = useMemo(() => {
    const fromEvent = get(event, 'selfiecam')?.language;
    if (fromEvent && typeof fromEvent === 'object') return fromEvent;
    if (!config) return null;
    const fromNested = get(config, 'language');
    if (fromNested && typeof fromNested === 'object') return fromNested;
    if (typeof config.language === 'object') return config.language;
    return null;
  }, [event?.slugDate, event?._id, event?.selfiecam, config]);

  useEffect(() => {
    const flat = languageObj ? flattenLanguage(languageObj) : [];
    setLanguageRowsAction(flat);
  }, [languageObj, setLanguageRowsAction]);

  useEffect(() => {
    const eventOrder = event?.availableLanguages;
    if (Array.isArray(eventOrder) && eventOrder.length > 0) {
      dispatch(setLocaleOrder(eventOrder));
    }
  }, [event?.availableLanguages, dispatch]);

  useEffect(() => {
    setChangesMap({});
  }, [languageObj, event?.slugDate]);

  const localesFromRows = useMemo(
    () => getLocalesFromRows(languageRows),
    [languageRows],
  );

  const availableLocales = useMemo(
    () => getAvailableLocales(localeOrder, localesFromRows),
    [localeOrder, localesFromRows],
  );

  const rowsForTable = useMemo(
    () => languageRows.map((row) => ({ ...row, id: row.path })),
    [languageRows],
  );

  const handleSaveCell = useCallback((oldValue, newValue, row, column) => {
    const { path } = row;
    const locale = column?.dataField;
    if (!locale) return;
    dispatch(updateLanguageCell({ path, locale, value: newValue }));
    setTableKey((k) => k + 1);
  }, [dispatch]);

  const handleDeleteLanguage = useCallback((localeToDelete) => {
    dispatch(deleteLanguage(localeToDelete));
  }, [dispatch]);

  const handleLocaleReorder = useCallback((newOrder) => {
    dispatch(setLocaleOrder(newOrder));
    setTableKey((k) => k + 1);
  }, [dispatch]);

  const headerFormatter = useCallback((column, colIndex, { filterElement }) => (
    <div className={style.ColumnHeaderContainer}>
      <div className={style.ColumnHeader}>
        <span>{column.text}</span>
        {availableLocales.length > 1 && (
          <RoundButton
            className={pageStyle.deleteLanguageBtn}
            title={`Delete ${column.text}`}
            onClick={() => handleDeleteLanguage(column.text)}
          >
            <FontAwesomeIcon icon={faCircleMinus} />
          </RoundButton>
        )}
      </div>
      {filterElement}
    </div>
  ), [availableLocales.length, handleDeleteLanguage]);

  const updateEventHandler = useCallback(async (eventIds) => {
    if (!eventIds?.length) throw new Error('No events selected to update.');

    const languages = groupedToLanguageObject(languageRows);
    await updateEventsGeneric(eventIds, {
      'selfiecam.language': languages,
      availableLanguages: availableLocales,
    });

    const slugDates = eventIds
      .map((id) => (template?._id === id ? template : allEvents.find((e) => e._id === id)))
      .filter(Boolean)
      .map((e) => e.slugDate)
      .filter(Boolean);
    const currentSlugDate = event?.slugDate || template?.slugDate;
    await Promise.all(slugDates.map(async (sd) => {
      try {
        const fresh = await fetchEvent(sd);
        dispatch(updateEventInStore(fresh));
        if (sd === currentSlugDate) {
          const sc = get(fresh, 'selfiecam');
          const langObj = sc?.language;
          if (langObj && typeof langObj === 'object') {
            const flatFromServer = flattenLanguage(langObj);
            setLanguageRowsAction(flatFromServer);
            setTableKey((k) => k + 1);
          }
          if (Array.isArray(fresh?.availableLanguages) && fresh.availableLanguages.length > 0) {
            dispatch(setLocaleOrder(fresh.availableLanguages));
          }
          setChangesMap({});
        }
      } catch (err) {
        console.error(err);
      }
    }));
  }, [
    languageRows,
    availableLocales,
    template,
    allEvents,
    dispatch,
    event?.slugDate,
    setLanguageRowsAction,
  ]);

  const columns = useMemo(() => [
    {
      text: 'Key',
      dataField: 'path',
      sort: true,
      filter: textFilter(),
      style: { minWidth: '180px' },
      editable: false,
    },
    ...availableLocales.map((locale) => ({
      text: locale,
      dataField: locale,
      sort: false,
      filter: textFilter(),
      style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      classes: (cell, row) => classnames('hover-edit-background', {
        [style.modifiedCell]: changesMap[row.path]?.[locale] === 'modified',
        [style.emptyCell]: changesMap[row.path]?.[locale] === 'empty',
      }),
      editable: true,
      headerFormatter,
      editorRenderer,
      formatter: (cell) => (
        <div className={style.cellWithPreview}>
          <span>{cell}</span>
          {looksLikeHtml(cell) && <HtmlPreviewButton value={cell} />}
        </div>
      ),
    })),
  ], [availableLocales, headerFormatter, changesMap]);

  const importHandler = useCallback((evt) => {
    const fileInput = evt.target;
    const file = fileInput.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ errors, data }) => {
        const criticalErrors = (errors || []).filter(
          (e) => e.type !== 'FieldMismatch' || e.code !== 'TooFewFields',
        );
        if (criticalErrors.length > 0) {
          displayErrorModal({
            html: (
              <>
                <p>Errors:</p>
                {criticalErrors.map(({ message, row }) => (
                  <p key={`${row}-${message}`}>
                    {message}
                    &nbsp;&nbsp;&nbsp;&nbsp;row:&nbsp;
                    {row + 2}
                  </p>
                ))}
              </>
            ),
          });
          return;
        }

        if (!data?.length) {
          displayErrorModal({ text: 'CSV file is empty.' });
          return;
        }

        const parseResult = parseCsvToLanguageImport(data, languageRows);
        const { mergedGrouped, changesMap: changesMapState, fileLocales } = parseResult;

        if (fileLocales.length === 0) {
          displayErrorModal({ text: 'No locale columns found in CSV.' });
          return;
        }

        setChangesMap(changesMapState);

        const flatRows = groupedToFlat(mergedGrouped);
        dispatch(setLanguageRows(flatRows));
        dispatch(setLocaleOrder(fileLocales));
        setTableKey((k) => k + 1);
        fileInput.value = '';
        displaySuccessModal({ text: 'Import successful! Update event to save your changes.' });
      },
    });
  }, [languageRows, dispatch]);

  return (
    <LanguagesPageShell loading={isLoading} updateHandler={updateEventHandler}>
      <ToolkitProvider
        keyField="id"
        data={rowsForTable}
        columns={columns}
        bootstrap4
        search
        exportCSV={{ fileName: `${event?.slugDate || 'selfiecam'}-languages.csv` }}
      >
        {(props) => (
          <div className={classnames(style.container, pageStyle.languagePage)}>
            <div className={style.header}>
              <h3 className="mb-0">Text Settings</h3>
              <div className={style.tableActionsContainer}>
                <Button
                  variant="outline-primary"
                  className="rounded-pill"
                  size="sm"
                  onClick={() => dispatch(openAddLanguageModal())}
                >
                  + Language
                </Button>
                <div className={style.fileInput}>
                  <FormFile
                    as={({ children }) => (
                      <Button variant="outline-primary" className="rounded-pill" size="sm">
                        {children}
                      </Button>
                    )}
                  >
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="ai-language-file-input">Import</label>
                    <FormFile.Input
                      id="ai-language-file-input"
                      accept="text/csv"
                      onChange={importHandler}
                    />
                  </FormFile>
                </div>
                <ExportCSVButton {...props.csvProps} className="rounded-pill btn btn-outline-primary btn-sm">
                  Export
                </ExportCSVButton>
              </div>
            </div>
            {availableLocales.length > 0 && (
              <div className={pageStyle.localeOrderBar}>
                <span className={pageStyle.localeOrderLabel}>
                  Language order (first = default):
                </span>
                <DragDropContext
                  onDragEnd={(result) => {
                    const { destination, source } = result;
                    if (!destination || destination.index === source.index) return;
                    const next = [...availableLocales];
                    const [removed] = next.splice(source.index, 1);
                    next.splice(destination.index, 0, removed);
                    handleLocaleReorder(next);
                  }}
                >
                  <Droppable droppableId="locale-order" direction="horizontal">
                    {(droppableProvided) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        className={pageStyle.localeOrderList}
                      >
                        {availableLocales.map((locale, index) => (
                          <Draggable key={locale} draggableId={locale} index={index}>
                            {(draggableProvided) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                {...draggableProvided.dragHandleProps}
                                className={pageStyle.localeOrderItem}
                              >
                                <FontAwesomeIcon
                                  icon={faGripVertical}
                                  className={pageStyle.localeOrderGrip}
                                />
                                <span>{locale}</span>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}
            <BootstrapTable
              key={tableKey}
              {...props.baseProps}
              wrapperClasses="table-responsive searchFilter react-bs-table-card"
              classes="card-table border-top"
              filter={filterFactory()}
              cellEdit={cellEditFactory({
                mode: 'click',
                blurToSave: true,
                afterSaveCell: handleSaveCell,
              })}
              noDataIndication={rowsForTable.length === 0 ? 'No language data.' : undefined}
            />
          </div>
        )}
      </ToolkitProvider>
      <AddLanguageModal />
    </LanguagesPageShell>
  );
};

export default connect(
  ({
    events: { selectedEvents, template, allEvents },
    aiTemplateLanguages: { languageRows, localeOrder },
  }) => ({
    slugDate: selectedEvents[0]?.slugDate,
    event: selectedEvents[0],
    template,
    allEvents,
    languageRows,
    localeOrder: localeOrder || [],
  }),
  { setLanguageRowsAction: setLanguageRows },
)(AiTemplateLanguage);
